import { useCallback, useEffect, useState } from 'react';
import { FileCheck, FileText, Download } from '../../icons';
// 채택 3단계 ② 인사 — 칸·숫자·단추를 정본 공통 컴포넌트로(B1·B2·D1).
import { Button, FormField, Input, NumberInput } from '../../components/shared';
import {
  createCertificate, listMyCertificates, getCertificateDocument,
  type CertificateDto, type CertificateDocumentDto, type CertKind,
} from '../../api/hr-certificate';
import { CertificateDoc } from './CertificateDoc';
// 기준 설정 (2026-08-28 · 판 7) — 종류의 «이름»과 «씀 여부»는 회사가 정한다.
//   못 읽으면 아래 기본 목록이 그대로 산다(깜빡이지 않는다).
import { useCompanyCodes } from '../../lib/company-codes';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';

/**
 * 증명서 발급 신청 — 직원 화면 (시안 B3, migration 129).
 *
 * ★★ **어느 쪽인지 고르기 전에 말한다.** 재직증명서는 그 자리에서 나오고(«바로 받기»),
 *    경력증명서는 담당자 확인을 탄다(«신청하기»). 눌러 보고서야 알게 되면 기다림이 생긴다.
 * ★ 신청이 발급으로 끝나면 **그 종이를 바로 보여 준다** — 목록에서 다시 찾게 하지 않는다.
 * ★ 인쇄는 브라우저에 맡긴다(PDF 라이브러리 없음). `@media print` 가 증명서만 남긴다.
 */
const KINDS: Array<{ key: CertKind; label: string; desc: string; instant: boolean }> = [
  { key: 'EMPLOYMENT', label: '재직증명서', desc: '지금 재직 중임을 증명한다', instant: true },
  { key: 'CAREER', label: '경력증명서', desc: '입사일 · 소속 · 직위를 담는다', instant: false },
];

const STATUS_LABEL: Record<string, string> = {
  ISSUED: '발급', REQUESTED: '대기', REJECTED: '반려',
};
const STATUS_TONE: Record<string, string> = {
  ISSUED: 'is-ok', REQUESTED: 'is-wait', REJECTED: 'is-no',
};

export function MyCertificatePage() {
  // ★ 회사가 고친 이름을 얹는다(「재직증명서」를 「재직확인서」로 부르는 곳이 있다).
  const codes = useCompanyCodes('hr');
  const kinds = codes.apply('certificate_kind', KINDS);
  const [kind, setKind] = useState<CertKind>('EMPLOYMENT');
  const [purpose, setPurpose] = useState('');
  const [recipient, setRecipient] = useState('');
  const [copies, setCopies] = useState(1);
  const [rows, setRows] = useState<CertificateDto[]>([]);
  const [doc, setDoc] = useState<CertificateDocumentDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');

  const reload = useCallback(async () => {
    const list = await listMyCertificates();
    setRows(list);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await listMyCertificates();
        if (!alive) return;
        setRows(list);
        setState('ok');
      } catch {
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, []);

  const openDoc = useCallback(async (id: string) => {
    try {
      setDoc(await getCertificateDocument(id));
      setError(null);
    } catch {
      setError('증명서를 불러오지 못했습니다.');
    }
  }, []);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await createCertificate({
        kind,
        purpose: purpose.trim() || null,
        recipient: recipient.trim() || null,
        copies,
      });
      await reload();
      // 그 자리에서 발급된 것은 바로 보여 준다 — 목록에서 다시 찾게 하지 않는다.
      if (created.status === 'ISSUED') await openDoc(created.id);
      else setDoc(null);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? '신청하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const picked = kinds.find((k) => k.key === kind) ?? KINDS[0]!;

  return (
    <div className="hrpage">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/me/certificates')}
        scope={scopeFor('/erp/hr/me/certificates')}
        title="증명서 발급 신청"
        description="재직 · 경력 증명서를 신청하고 받습니다"
      />

      {state === 'folded' && (
        <div className="hrpage__note">증명서 기능을 불러오지 못했습니다 — 서버가 아직 준비되지 않았을 수 있습니다.</div>
      )}

      {state !== 'folded' && (
        <>
          <div className="hrpage__grid">
            {kinds.map((k) => (
              <button
                type="button"
                key={k.key}
                className={`hrcert__kind${kind === k.key ? ' is-on' : ''}`}
                onClick={() => setKind(k.key)}
                aria-pressed={kind === k.key}
              >
                {k.instant ? <FileCheck size={20} aria-hidden /> : <FileText size={20} aria-hidden />}
                <span className="hrcert__kindnm">{k.label}</span>
                <span className="hrcert__kinddesc">{k.desc}</span>
                <span className={`hrpage__pill ${k.instant ? 'is-ok' : 'is-wait'}`}>
                  {k.instant ? '즉시 발급' : '담당자 확인 후'}
                </span>
              </button>
            ))}
          </div>

          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              신청 정보
              <span className="hrpage__panels">용도 · 제출처는 기록만 합니다 — 비어도 발급됩니다</span>
            </h2>
            <div className="hrcert__form">
              <FormField label="용도" className="hrcert__field">
                <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} maxLength={100}
                  placeholder="예) 금융기관 제출" fullWidth />
              </FormField>
              <FormField label="제출처" className="hrcert__field">
                <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} maxLength={100}
                  placeholder="예) 국민은행 역삼지점" fullWidth />
              </FormField>
              <FormField label="부수" className="hrcert__field hrcert__field--n">
                <NumberInput value={copies} min={1} max={10} steppers
                  onValueChange={(v) => setCopies(Math.min(10, Math.max(1, v ?? 1)))} fullWidth />
              </FormField>
              <Button variant="primary" onClick={submit} disabled={busy} loading={busy}>
                {busy ? '보내는 중…' : picked.instant ? '바로 받기' : '신청하기'}
              </Button>
            </div>
            {error && <p className="hrcert__err" role="alert">{error}</p>}
          </section>

          {doc && (
            <section className="hrpage__panel hrcert__docwrap">
              <h2 className="hrpage__panelt">
                발급된 증명서
                <span className="hrpage__panels">브라우저 인쇄로 종이·PDF 를 만듭니다</span>
                <Button variant="secondary" size="compact" className="hrcert__printbtn"
                  onClick={() => window.print()} iconLeft={<Download size={14} aria-hidden />}>
                  인쇄 · PDF
                </Button>
              </h2>
              <div className="hrcert__docbody">
                <CertificateDoc doc={doc} />
              </div>
            </section>
          )}

          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">내 발급 이력</h2>
            {rows.length === 0 ? (
              <p className="hrpage__empty">아직 신청한 증명서가 없습니다.</p>
            ) : (
              <table className="hrpage__tbl">
                <thead>
                  <tr>
                    <th>신청일</th><th>종류</th><th>용도 · 제출처</th>
                    <th className="hrpage__num">부수</th><th className="hrpage__act">상태</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.createdAt.slice(0, 10)}</td>
                      <td>
                        {r.kindLabel}
                        {r.docNo ? <span className="hrpage__dim"> · {r.docNo}</span> : null}
                      </td>
                      <td>
                        {[r.purpose, r.recipient].filter(Boolean).join(' · ') || <span className="hrpage__dim">—</span>}
                        {r.rejectReason ? <span className="hrpage__dim"> · {r.rejectReason}</span> : null}
                      </td>
                      <td className="hrpage__num">{r.copies}</td>
                      <td className="hrpage__act">
                        <span className={`hrpage__pill ${STATUS_TONE[r.status] ?? ''}`}>
                          {STATUS_LABEL[r.status] ?? r.status}
                        </span>
                        {r.status === 'ISSUED' ? (
                          <Button variant="ghost" size="sm" onClick={() => openDoc(r.id)}>보기</Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
